import { useState, useMemo } from "react";
import { num, toM, m2, money, UNITS, CalcLayout, FormCol, Card, Field, Sel, HowTo, Advanced, ResultCard, useCatalog, ProductSelect } from "./shared";

export default function HuleEspuma({ onSave, s }) {
  const items = useCatalog("hule_espuma");
  const [pid, setPid] = useState("");
  const prod = items.find((p) => p.id === pid);
  const [u, setU] = useState("m");
  const [proyecto, setProyecto] = useState("Cojín");
  const [piezas, setPiezas] = useState("4");
  const [largo, setLargo] = useState("0.50");
  const [ancho, setAncho] = useState("0.50");
  const [espesor, setEspesor] = useState("0.10");
  const [densidad, setDensidad] = useState("30");
  const [margen, setMargen] = useState("5");
  const [placaLargo, setPlacaLargo] = useState("2.00");
  const [placaAncho, setPlacaAncho] = useState("1.20");
  const [placaEspesor, setPlacaEspesor] = useState("0.10");
  const [precioPlaca, setPrecioPlaca] = useState("");
  const pr = prod ? prod.price : num(precioPlaca);

  const r = useMemo(() => {
    const n = Math.max(1, Math.round(num(piezas)) || 1);
    const f = 1 + num(margen) / 100;
    const cL = toM(largo, u) * f, cW = toM(ancho, u) * f, cE = toM(espesor, u);
    const pL = toM(placaLargo, u), pW = toM(placaAncho, u), pE = toM(placaEspesor, u);
    const capas = pE > 0 ? Math.ceil(cE / pE) : 1;
    const fit = (a, b, c, d) => a > 0 && c > 0 ? Math.floor(a / c) * Math.floor(b / d) : 0;
    const opt1 = fit(pL, pW, cL, cW);
    const opt2 = fit(pL, pW, cW, cL);
    const piezasPorPlaca = Math.max(opt1, opt2);
    const placasBase = piezasPorPlaca > 0 ? Math.ceil(n / piezasPorPlaca) : 0;
    const placas = placasBase * capas;
    const areaUtil = n * cL * cW;
    const areaPlaca = pL * pW;
    const aprov = placasBase > 0 && areaPlaca > 0 ? (areaUtil / (placasBase * areaPlaca)) * 100 : 0;
    const volPieza = cL * cW * cE;
    const volTotal = volPieza * n;
    const peso = volTotal * num(densidad);
    const cMat = placas * pr;
    const total = cMat;
    const w = [];
    if (piezasPorPlaca === 0) w.push("La pieza no cabe en la placa seleccionada: elige una placa más grande o corte a medida.");
    if (capas > 1) w.push(`El espesor requiere ${capas} capas de placa (laminación).`);
    if (prod && prod.stock < placas) w.push(`Inventario insuficiente de "${prod.name}": stock ${prod.stock}, requieres ${placas} placas.`);
    const rows = [
      { section: "Proyecto" },
      { label: "Tipo", value: proyecto }, { label: "Piezas", value: `${n}` },
      { label: "Medida final", value: `${toM(largo, u).toFixed(2)} × ${toM(ancho, u).toFixed(2)} × ${toM(espesor, u).toFixed(2)} m` },
      { label: "Densidad", value: `${num(densidad)} kg/m³` }, { label: "Margen de corte", value: `${num(margen)}%` },
      { section: "Optimización de corte" },
      { label: "Medida de corte", value: `${cL.toFixed(3)} × ${cW.toFixed(3)} m` },
      { label: "Piezas por placa", value: `${piezasPorPlaca}` }, { label: "Capas por espesor", value: `${capas}` },
      { label: "Placas requeridas", value: `${placas} placa(s)`, strong: true },
      { label: "Aprovechamiento", value: `${aprov.toFixed(0)}%` },
      { section: "Volumen y peso" },
      { label: "Volumen por pieza", value: `${volPieza.toFixed(4)} m³` }, { label: "Volumen total", value: `${volTotal.toFixed(4)} m³` },
      { label: "Peso estimado", value: `${peso.toFixed(2)} kg` }, { label: "Área utilizada", value: m2(areaUtil) },
      { section: "Costos" },
      { label: "Materiales", value: money(cMat) }, { label: "Total estimado", value: money(total), strong: true },
    ];
    return { rows, warnings: w, total, inputs: { u, proyecto, piezas } };
  }, [u, proyecto, piezas, largo, ancho, espesor, densidad, margen, placaLargo, placaAncho, placaEspesor, pr, prod]);

  return (
    <CalcLayout>
      <FormCol>
        <HowTo title="¿Cómo calcular hule espuma?"><p>Se aplica el margen de corte a la medida final, se optimiza el corte sobre la placa (probando pieza normal y girada), y se calcula el número de placas, capas por espesor, volumen, peso y aprovechamiento.</p></HowTo>
        <Card>
          <Sel label="Unidad" value={u} onChange={setU} opts={UNITS} testid="he-unidad" />
          <Sel label="Tipo de proyecto" value={proyecto} onChange={setProyecto} opts={["Cojín", "Asiento", "Colchoneta", "Colchón", "Respaldo", "Panel acústico", "Empaque", "Otro"]} testid="he-proyecto" />
          <Field label="Número de piezas" value={piezas} onChange={setPiezas} step="1" min="1" testid="he-piezas" />
          <Sel label="Densidad (kg/m³)" value={densidad} onChange={setDensidad} opts={["15", "20", "25", "30", "35", "40"]} testid="he-densidad" />
          <Field label="Largo final" value={largo} onChange={setLargo} suffix={u} testid="he-largo" />
          <Field label="Ancho final" value={ancho} onChange={setAncho} suffix={u} testid="he-ancho" />
          <Field label="Espesor final" value={espesor} onChange={setEspesor} suffix={u} testid="he-espesor" />
        </Card>
        <ProductSelect items={items} value={pid} onChange={setPid} testid="he-product" />
        <Card>
          <Field label="Placa: largo" value={placaLargo} onChange={setPlacaLargo} suffix={u} testid="he-placalargo" />
          <Field label="Placa: ancho" value={placaAncho} onChange={setPlacaAncho} suffix={u} testid="he-placaancho" />
          <Field label="Placa: espesor" value={placaEspesor} onChange={setPlacaEspesor} suffix={u} testid="he-placaespesor" />
        </Card>
        <Advanced>
          <Field label="Margen de corte (%)" value={margen} onChange={setMargen} step="1" suffix="%" testid="he-margen" />
          <Field label="Precio por placa ($)" value={prod ? prod.price : precioPlaca} onChange={setPrecioPlaca} suffix="$" testid="he-precio" />
        </Advanced>
      </FormCol>
      <ResultCard title="Cotización de Hule Espuma — GOÛT" modality="hule_espuma" rows={r.rows} warnings={r.warnings} total={r.total} inputs={r.inputs} onSave={onSave} />
    </CalcLayout>
  );
}
